import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

/**
 * VideoPlayer with anti-abandon checks and checkpoints.
 * props: courseId, module (with attention_checks, checkpoints), moduleIndex, onComplete
 */
export default function VideoPlayer({ courseId, module, moduleIndex, onComplete }) {
  const videoRef = useRef(null);
  const lastReportRef = useRef(0);
  const firedAttention = useRef(new Set());
  const firedCheckpoint = useRef(new Set());
  const [attentionPrompt, setAttentionPrompt] = useState(null);
  const [checkpointPrompt, setCheckpointPrompt] = useState(null);
  const [attentionTimer, setAttentionTimer] = useState(0);
  const attentionRef = useRef(null);

  const videoSrc = module?.video_url || (module?.video_path ? `${api.defaults.baseURL}/files/${module.video_path}` : "");

  const reportProgress = useCallback(async (seconds, completed = false) => {
    try {
      await api.post(`/courses/${courseId}/progress`, {
        module_index: moduleIndex,
        seconds_watched: Math.floor(seconds),
        completed,
      });
    } catch {}
  }, [courseId, moduleIndex]);

  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const t = v.currentTime;

    // report every 10s
    if (t - lastReportRef.current >= 10) {
      lastReportRef.current = t;
      reportProgress(t);
    }

    // attention checks
    (module?.attention_checks || []).forEach((ac, idx) => {
      const key = `att-${idx}`;
      if (t >= ac.timestamp_seconds && !firedAttention.current.has(key)) {
        firedAttention.current.add(key);
        v.pause();
        setAttentionPrompt({ ...ac, idx });
        setAttentionTimer(ac.timeout_seconds || 15);
      }
    });

    // checkpoints
    (module?.checkpoints || []).forEach((cp, idx) => {
      const key = `cp-${idx}`;
      if (t >= cp.timestamp_seconds && !firedCheckpoint.current.has(key)) {
        firedCheckpoint.current.add(key);
        v.pause();
        setCheckpointPrompt({ ...cp, idx, startedAt: Date.now() });
      }
    });
  }, [module, reportProgress]);

  // Attention timer
  useEffect(() => {
    if (attentionPrompt == null) return;
    if (attentionTimer <= 0) {
      // failure
      api.post(`/courses/${courseId}/attention`, {
        module_index: moduleIndex,
        timestamp_seconds: attentionPrompt.timestamp_seconds,
        responded: false,
      }).catch(() => {});
      toast.error("Atenção não confirmada. A ocorrência foi registrada.");
      setAttentionPrompt(null);
      // require new confirmation - rewind 5s and play
      const v = videoRef.current;
      if (v) {
        v.currentTime = Math.max(0, attentionPrompt.timestamp_seconds - 5);
        firedAttention.current.delete(`att-${attentionPrompt.idx}`);
      }
      return;
    }
    attentionRef.current = setTimeout(() => setAttentionTimer((t) => t - 1), 1000);
    return () => clearTimeout(attentionRef.current);
  }, [attentionPrompt, attentionTimer, courseId, moduleIndex]);

  const confirmAttention = async () => {
    await api.post(`/courses/${courseId}/attention`, {
      module_index: moduleIndex,
      timestamp_seconds: attentionPrompt.timestamp_seconds,
      responded: true,
    }).catch(() => {});
    toast.success("Presença confirmada");
    setAttentionPrompt(null);
    videoRef.current?.play();
  };

  const submitCheckpoint = async (selectedIdx) => {
    const responseTime = (Date.now() - checkpointPrompt.startedAt) / 1000;
    try {
      const { data } = await api.post(`/courses/${courseId}/checkpoint`, {
        module_index: moduleIndex,
        checkpoint_index: checkpointPrompt.idx,
        selected_option: selectedIdx,
        response_time_seconds: responseTime,
      });
      if (data.correct) toast.success("Resposta correta!");
      else toast.error("Resposta incorreta");
    } catch {}
    setCheckpointPrompt(null);
    videoRef.current?.play();
  };

  const handleEnded = () => {
    reportProgress(videoRef.current?.duration || 0, true);
    toast.success("Módulo concluído");
    onComplete && onComplete();
  };

  if (!videoSrc) {
    return (
      <div className="bg-slate-900 text-white aspect-video flex items-center justify-center rounded-lg">
        Nenhum vídeo carregado para este módulo.
      </div>
    );
  }

  return (
    <div className="relative">
      <video
        ref={videoRef}
        src={videoSrc}
        controls
        controlsList="nodownload"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        className="w-full rounded-lg bg-black aspect-video"
        data-testid="course-video-player"
      />

      <Dialog open={!!attentionPrompt} onOpenChange={() => {}}>
        <DialogContent data-testid="attention-dialog" className="max-w-md" onEscapeKeyDown={(e)=>e.preventDefault()} onPointerDownOutside={(e)=>e.preventDefault()} onInteractOutside={(e)=>e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--unigr-red)]">
              <AlertTriangle className="w-5 h-5" /> Verificação de presença
            </DialogTitle>
            <DialogDescription>
              {attentionPrompt?.message || "Confirme sua presença para continuar o treinamento."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 text-sm text-slate-600">
            Tempo restante: <span className="font-mono font-bold text-[var(--unigr-text)]">{attentionTimer}s</span>
          </div>
          <Button onClick={confirmAttention} className="btn-navy mt-2" data-testid="confirm-attention-btn">
            Confirmar presença
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!checkpointPrompt} onOpenChange={() => {}}>
        <DialogContent data-testid="checkpoint-dialog" className="max-w-lg" onEscapeKeyDown={(e)=>e.preventDefault()} onPointerDownOutside={(e)=>e.preventDefault()} onInteractOutside={(e)=>e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display">Pergunta no vídeo</DialogTitle>
            <DialogDescription className="text-base text-[var(--unigr-text)]">
              {checkpointPrompt?.question}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 mt-2">
            {(checkpointPrompt?.options || []).map((opt, i) => (
              <button
                key={i}
                onClick={() => submitCheckpoint(i)}
                data-testid={`checkpoint-option-${i}`}
                className="text-left px-4 py-3 rounded-md border border-[var(--unigr-border)] hover:border-[var(--unigr-navy)] hover:bg-slate-50 transition-colors"
              >
                {opt.text}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
